import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
} from "react";
import styles from "./Actions.module.scss";
import "./actions.css";
import Button from "../../../../components/button/Button";
import { useNextContent } from "../../useNextContent";
import PauseModal from "../../pause-modal/PauseModal";
import CompleteModal from "../../complete-modal/CompleteModal";
import { useAnalytics } from "../../../../hooks/useAnalytics";
import { Events } from "../../../../services/Events";
import {
  ContentType,
  useCancelFeedbackFormMutation,
  useHeaConfirmLessonMutation,
  useUpdateLastVisitedLessonMutation,
} from "@api/graphql-codegen";
import { useCourseContent } from "@hooks/useCourseContent";
import InfoCircleIcon from "@src/assets/icons/infocircle.svg?react";
import TicketIcon from "src/assets/icons/ticket.svg?react";
import WarningIcon from "src/assets/icons/red-warning.svg?react";
import OutlineIcon from "src/assets/icons/outline.svg?react";
import CloseIcon from "src/assets/icons/close.svg?react";
import CheckIcon from "src/assets/icons/complete_check.svg?react";
import CrossIcon from "src/assets/icons/complete_uncheck.svg?react";

import { create, CreateTypes } from "canvas-confetti";
import { createURL, isHomeEDApp, SURVEY_TYPE } from "@src/constants";
import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
  useAppNotification,
  useExitTicketLocalStorage,
  useMarkLessonComplete,
  useUser,
} from "@src/hooks";
import AdditionalModal from "../../additional-modal/AdditionalModal";
import { ProgressAfterConfetti } from "./ProgressAfterConfetti";
import { ExtendedQuiz, FeedbackForm } from "@src/types";
import Spinner from "@src/components/spinner/Spinner";
import AutoCompleteModal from "../../complete-modal/AutoCompleteModal";
import { useLessonFeedbackStatusStorage } from "@src/hooks/useLessonFeedbackStatusStorage";
import { logger } from '../../../../utils/logger';

const CopyToClipboardComponent = CopyToClipboard as React.ComponentType<any>;

type Props = {
  children: React.ReactNode;
  fullScreen: boolean;
  leftButton?: React.ReactNode;
  rightButton?: React.ReactNode;
  totalPages?: number;
  pageNumber?: number;
  className?: string;
  hideNextButton?: boolean;
  isPreview?: boolean;
  exitTicket?: boolean;
  goBack?: () => void;
  formData?: string | null;
  videoEnd?: boolean;
  handlePauseVideo?: () => void;
  fromModal?: boolean;
};

function Actions(props: Props) {
  const {
    children,
    rightButton,
    leftButton,
    totalPages,
    pageNumber,
    className = "",
    hideNextButton,
    isPreview = false,
    exitTicket = false,
    goBack,
    videoEnd,
    handlePauseVideo,
    formData,
    fromModal = false,
    fullScreen,
  } = props;
  const {
    hasNextContent,
    selectNext,
    selectPrev,
    selectExitTicket,
    hasAdditionalContent,
    hasPrevContent,
    contentNext,
    isParentApp,
  } = useNextContent();
  const {
    section,
    startedAlternateLesson,
    currentUnit,
    currentHeaUnit,
    lesson,
    content,
    lastContent,
    program,
    exitTickets,
    setExitTickets,
    setShowExitModal,
    currentStudyplan,
    accountLocked,
    showExitTicket,
    isViewExitTicket,
    setShowReview,
    completeModal,
    setCompleteModal,
    isStudyGuide,
    isStudentQuiz,
    currentQuiz,
    setCurrentQuiz,
    myQuizzes,
    setMyQuizzes,
    isNewLessonLayout,
  } = useCourseContent();

  const { trackEvent } = useAnalytics();
  const { displayToastMessage } = useAppNotification();
  const { getFormId, removeFormId } = useExitTicketLocalStorage();
  const { setFeedbackStatusWithLesson } = useLessonFeedbackStatusStorage();
  const [user] = useUser();

  const canvasRef = useRef<HTMLCanvasElement>(null),
    fireRef = useRef<CreateTypes | null>(null);
  const [pauseModal, setPauseModal] = useState(false);
  const [showReturningUnit, setShowReturningUnit] = useState(false);

  const [showRememberNotify, setShowRememberNotify] = useState(false);
  const [showHereModal, setShowHereModal] = useState(false);

  const [hideCanvas, setHideCanvas] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const [lastPage, setLastPage] = useState(0);

  const [congratesTriggered, setCongratesTriggered] = useState(false);

  const exitTicketActive = useMemo(() => {
    let tickets = exitTickets.filter(
      (ticket) => ticket.lessonNum === lesson?.number
    );
    return tickets.length > 0 ? tickets[0] : null;
  }, [exitTickets, lesson?.number]);

  const jsonFormData = useMemo(() => {
    if (!formData) return null;
    try {
      return JSON.parse(formData) as FeedbackForm;
    } catch (e) {
      logger.info("parse error-", e, formData);
      return null;
    }
  }, [formData]);

  const formTypeExitTicket = useMemo(
    () =>
      Number(jsonFormData?.surveyType) === SURVEY_TYPE.EXIT_TICKET
        ? true
        : false,
    [jsonFormData]
  );
  const formTypePreProgram = useMemo(
    () =>
      Number(jsonFormData?.surveyType) === SURVEY_TYPE.NA_PRE_PROGRAM_QUIZ
        ? true
        : false,
    [jsonFormData]
  );

  const lastmodule = useMemo(() => {
    const modules = [...(lesson?.modules || [])];
    if (modules.length > 2) {
      const index = modules.findIndex((m) => m?.id === content?.id);
      if (
        index === modules.length - 2 &&
        modules.at(modules.length - 1)?.lastInLesson
      ) {
        return true;
      }
    }
    return false;
  }, [content, lesson]);
  const width = useMemo(
    () =>
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth,
    []
  );
  const height = useMemo(() => window.innerHeight, []);

  const startCountDown = useCallback(() => {
    setShowReturningUnit(true);
  }, []);

  const onMarkLessonCompleted = useCallback(() => {
    if (accountLocked && !isViewExitTicket) {
      setTimeout(() => {
        startCountDown();
      }, 2000);
    } else {
      setCompleteModal(false);
      setIsComplete(true);
      if (isViewExitTicket) {
        displayToastMessage("Completed Lesson successfully!");
      }
      if (lesson?.id) {
        setFeedbackStatusWithLesson(lesson?.id, "Enable");
      }
      goBack?.();
    }
  }, [
    accountLocked,
    lesson?.id,
    displayToastMessage,
    isViewExitTicket,
    startCountDown,
    goBack,
    setCompleteModal,
    setFeedbackStatusWithLesson,
  ]);
  const {
    markLessonComplete,
    lessonNotStarted,
    markStudyReviewComplete,
    studyReviewNotStarted,
    loading: isMarkLessonCompleteLoading,
  } = useMarkLessonComplete(
    onMarkLessonCompleted,
    goBack,
    startedAlternateLesson ? section : undefined
  );

  const handleCompleteLesson = useCallback(async () => {
    if ((currentHeaUnit || currentUnit) && lesson) {
      const unitName = isHomeEDApp ? currentHeaUnit?.name : currentUnit?.name;
      const unitId = isHomeEDApp ? currentHeaUnit?.id : currentUnit?.id;
      await markLessonComplete(
        isHomeEDApp ? currentHeaUnit!.id : currentUnit!.id,
        lesson?.id,
        currentStudyplan?.id,
        {
          unit_name: unitName,
          unit_id: unitId,
          course_name: program?.name || undefined, //unitName === 'Educator Onboarding' ? 'Educator Onboarding' : (unitName === 'Educator Wellbeing' ? 'Educator Wellbeing' : program?.name),
          course_id: program?.id || undefined,
          lesson_name: lesson?.name,
          lesson_id: lesson?.id,
          lesson_position: lesson?.number || undefined,
          study_plan_id: currentStudyplan?.id,
          study_plan_name: currentStudyplan?.name,
          section_id: section?.id,
        },
        (lesson as any)?.isOnlyNotCompletedLesson,
        currentUnit?.progress,
        isHomeEDApp && accountLocked && !isViewExitTicket
          ? true
          : !isHomeEDApp
          ? true
          : false
      );
      if (startedAlternateLesson) {
        goBack?.();
      }
    }
    if (currentUnit && isStudyGuide) {
      await markStudyReviewComplete(currentUnit!.id);
    }
  }, [
    isStudyGuide,
    currentHeaUnit,
    currentUnit,
    lesson,
    markLessonComplete,
    markStudyReviewComplete,
    currentStudyplan?.id,
    currentStudyplan?.name,
    program,
    section?.id,
    accountLocked,
    isViewExitTicket,
    startedAlternateLesson,
    goBack,
  ]);

  useEffect(() => {
    if (canvasRef.current) {
      fireRef.current = create(canvasRef.current, {} as any);
    }
  }, []);
  const fire = useCallback(() => {
    if (fireRef.current) {
      fireRef.current({
        particleCount: 1200,
        shapes: ["circle", "square", "circle"],
        startVelocity: 50,
        spread: 360,
        origin: {
          x: Math.random(),
          y: Math.random() - 0.1,
        },
      });
    }
  }, []);

  const handleCongrates = useCallback(() => {
    setHideCanvas(false);
    if (isHomeEDApp && isViewExitTicket) {
      handleCompleteLesson();
      return;
    }
    if (!isPreview || isNewLessonLayout) {
      fire();
      setTimeout(() => {
        fire();
        setTimeout(() => {
          fire();
          setTimeout(() => {
            fire();
          }, 500);
        }, 500);
      }, 500);
    }
    setTimeout(() => {
      setHideCanvas(true);
    }, 10000);
    setTimeout(() => {
      setTimeout(() => {
        setShowRememberNotify(true);
        setTimeout(() => {
          // setShowStudentNotify(true);
          setTimeout(() => {
            setShowRememberNotify(false);
            // setTimeout(() => {
            //   setShowStudentNotify(false);
            // }, 5000)
          }, 10000);
        }, 5000);
      }, 5000);
      if (isHomeEDApp && accountLocked) {
        handleCompleteLesson();
      }
    }, 1000);
  }, [
    isViewExitTicket,
    isPreview,
    isNewLessonLayout,
    fire,
    accountLocked,
    handleCompleteLesson,
  ]);

  const [heaConfirmLesson, { loading }] = useHeaConfirmLessonMutation();
  const [updateLastVisitedLesson] = useUpdateLastVisitedLessonMutation({
    onError(e) {
      logger.debug(`[e]`, e);
    },
  });

  const [cancelFeedbackForm, { loading: cancelling }] =
    useCancelFeedbackFormMutation({
      onError(e) {
        logger.debug(`[e]`, e);
      },
      onCompleted(data) {
        if (data.cancelFeedbackForm) {
          removeFormId(lesson?.id);
          const tickets = exitTickets.map((ticket) => {
            if (lesson?.number === ticket.lessonNum) {
              return {
                ...ticket,
                status: false,
                exitTicketCount: 0,
                expiryMinutes: 0,
                copyLink: "",
              };
            }
            return ticket;
          });
          setExitTickets(tickets);
        }
        if (isStudentQuiz) {
          const updatedQuiz = {
            ...currentQuiz,
            feedbackForm: undefined,
            expiryMinutes: 0,
            status: false,
          };
          setCurrentQuiz(updatedQuiz as ExtendedQuiz);
          const updateQuizzes = myQuizzes.map((quiz: any, i: number) => {
            if (quiz.id === updatedQuiz?.id) {
              return {
                ...quiz,
                feedbackForm: undefined,
                expiryMinutes: 0,
                status: false,
              };
            } else {
              return quiz;
            }
          });
          setMyQuizzes(updateQuizzes);
        }
      },
    });
    const isLastPage = useMemo(() => {
      if (pageNumber) {
        setLastPage(pageNumber);
        return (((totalPages === pageNumber) && (lastPage === (totalPages - 1))) || ((totalPages === pageNumber && totalPages === 1)))
      }
      return false
    }, [totalPages, pageNumber, lastPage])
  
    useEffect(() => {
      if (isLastPage && !congratesTriggered) {
        if (content?.lastInLesson && !hasNextContent && (!isPreview || isNewLessonLayout)) {
          setCongratesTriggered(true);
          setTimeout(() => {
            handleCongrates();
          }, 500)
        }
      } else if (!congratesTriggered) {
        if (lastContent && content?.lastInLesson && !hasNextContent && (!isPreview || isNewLessonLayout)) {
          setCongratesTriggered(true);
          setTimeout(() => {
            handleCongrates();
          }, 500)
        }
      }
    }, [content, congratesTriggered, hasNextContent, isLastPage, isPreview, lastContent, videoEnd, isNewLessonLayout, handleCongrates])

    useEffect(() => {
      if (content?.additionalAfter && hasNextContent && !isPreview) {
        if (content.type === ContentType.VIDEO && videoEnd) {
          setShowHereModal(true)
        }
      }
    }, [content, hasNextContent, isPreview, videoEnd])

  const handleClosePauseModal = useCallback(() => {
    setPauseModal(false);
  }, []);

  const handlePause = useCallback(() => {
    if (
      !isHomeEDApp &&
      contentNext &&
      (!isPreview || isNewLessonLayout) &&
      !isParentApp &&
      contentNext.unitId
    ) {
      updateLastVisitedLesson({
        variables: {
          input: {
            ...contentNext,
          },
        },
      });
    }
    handleClosePauseModal();
  }, [
    handleClosePauseModal,
    updateLastVisitedLesson,
    contentNext,
    isParentApp,
    isPreview,
    isNewLessonLayout,
  ]);

  const handleContinue = useCallback(() => {
    selectNext();
    handleClosePauseModal();
  }, [handleClosePauseModal, selectNext]);

  const handleGoBack = useCallback(() => {
    setShowHereModal(false);
  }, []);

  const handleAdditional = useCallback(() => {
    setShowHereModal(false);
    selectNext();

    trackEvent(Events.actions.extensionLessonStarted, {
      lesson: lesson?.name,
      unit: currentUnit?.name || currentHeaUnit?.name,
      course: program?.name,
      schoolId: user?.schools?.[0]?.id,
      schoolName: user?.schools?.[0]?.name,
      userId: user?.id,
      userName: `${user?.firstName} ${user?.lastName}`,
    });
  }, [
    currentHeaUnit?.name,
    currentUnit?.name,
    lesson?.name,
    program?.name,
    selectNext,
    trackEvent,
    user,
  ]);

  const gotoExitTicket = useCallback(() => {
    setShowHereModal(false);
    selectExitTicket();
  }, [selectExitTicket]);

  const handleConfirmComplete = useCallback(async () => {
    if (!currentStudyplan?.id || !lesson?.id) {
      return;
    }
    const res = await heaConfirmLesson({
      variables: {
        input: {
          studyplanId: currentStudyplan.id,
          lessonId: lesson.id,
          completed: true,
        },
      },
    });
    if (res?.data?.heaConfirmLesson) {
      if (!isViewExitTicket) {
        handleCongrates();
        setTimeout(() => {
          startCountDown();
        }, 4000);
      } else {
        displayToastMessage("Lesson confirmed successfully!");
        goBack?.();
      }
    }
  }, [
    currentStudyplan?.id,
    lesson?.id,
    heaConfirmLesson,
    isViewExitTicket,
    handleCongrates,
    startCountDown,
    displayToastMessage,
    goBack,
  ]);

  const handleInComplete = useCallback(() => {
    goBack?.();
    setShowReview(true);
  }, [goBack, setShowReview]);

  const handleComplete = useCallback(() => {
    if (startedAlternateLesson && section?.id && !(isComplete || isPreview)) {
      setTimeout(() => {
        setCompleteModal(true);
      }, 500);
      return;
    }
    if (
      (isComplete ||
        (isPreview && !isNewLessonLayout) ||
        isStudentQuiz ||
        !(content?.lastInLesson || !hasNextContent)) &&
      goBack
    ) {
      // check if last module
      goBack();
    } else {
      if (isHomeEDApp && (accountLocked || isParentApp) && goBack) {
        goBack();
      } else {
        setCompleteModal(true);
      }
    }
  }, [
    startedAlternateLesson,
    content,
    isComplete,
    isPreview,
    isNewLessonLayout,
    goBack,
    setCompleteModal,
    hasNextContent,
    accountLocked,
    isParentApp,
    section?.id,
    isStudentQuiz,
  ]);

  const handleGenerateExitLink = useCallback(() => {
    setShowExitModal(true);
  }, [setShowExitModal]);

  useEffect(() => {
    const handleTabClose = (event: {
      preventDefault: () => void;
      returnValue: string;
    }) => {
      event.preventDefault();
      if (!content?.lastInLesson) {
        return "";
      }
      return (event.returnValue = "Are you sure you want to exit?");
    };

    window.addEventListener("beforeunload", handleTabClose);
    return () => {
      window.removeEventListener("beforeunload", handleTabClose);
    };
  }, [content?.lastInLesson]);

  const handleNotStartedLesson = useCallback(async () => {
    if (startedAlternateLesson) {
      goBack?.();
    }
    if (currentUnit && lesson) {
      await lessonNotStarted(currentUnit!.id, lesson?.id);
    }
    if (currentUnit && isStudyGuide) {
      await studyReviewNotStarted(currentUnit.id);
    }
  }, [
    isStudyGuide,
    currentUnit,
    lesson,
    lessonNotStarted,
    studyReviewNotStarted,
    startedAlternateLesson,
    goBack,
  ]);

  const handleCloseCounter = () => {
    goBack?.();
  };
  const handlePrev = useCallback(() => {
    if (isViewExitTicket) {
      goBack?.();
    }
    selectPrev();
  }, [selectPrev, goBack, isViewExitTicket]);

  const onNext = useCallback(() => {
    if (
      content?.additionalAfter &&
      hasNextContent &&
      !isPreview &&
      hasAdditionalContent
    ) {
      if (handlePauseVideo) {
        handlePauseVideo();
      }
      setShowHereModal(true);
      return;
    }
    // ToDo: temporarily comment out pause modal
    // if (nextContent && nextContent.pauseHere) {
    //   setPauseModal(true);
    // } else {
    // if (lastmodule) {
    //   handleComplete();
    // } else {
    selectNext();
    // }
    // }
    trackEvent(Events.actions.contentContinueButtonPressed, {
      pageNumber,
      content: content?.name,
      lesson: lesson?.name,
      unit: currentUnit?.name || currentHeaUnit?.name,
      course: program?.name,
      schoolId: user?.schools?.[0]?.id,
      schoolName: user?.schools?.[0]?.name,
      userId: user?.id,
      userName: `${user?.firstName} ${user?.lastName}`,
    });
  }, [
    content?.additionalAfter,
    content?.name,
    hasNextContent,
    isPreview,
    hasAdditionalContent,
    selectNext,
    trackEvent,
    pageNumber,
    lesson?.name,
    currentUnit?.name,
    currentHeaUnit?.name,
    program?.name,
    handlePauseVideo,
    user,
  ]);

  const survey = useMemo(() => {
    // Exit Ticket
    if (content?.type === ContentType.FORM && showExitTicket) {
      return true;
    } else {
      return false;
    }
  }, [content, showExitTicket]);

  useEffect(() => {
    if (exitTicketActive?.expiryMinutes === 0 && exitTicketActive?.status) {
      const formId = getFormId(lesson?.id);
      if (formId) {
        cancelFeedbackForm({
          variables: {
            input: { id: formId },
          },
        });
      }
    }
  }, [lesson, exitTicketActive, cancelFeedbackForm, getFormId]);

  const generateExitTicket = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    setShowExitModal(true);
  };

  const cancelExitTicket = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    const formId = isStudentQuiz
      ? currentQuiz?.feedbackForm?.id
      : getFormId(lesson?.id);
    if (formId) {
      cancelFeedbackForm({
        variables: {
          input: { id: formId },
        },
      });
    }
  };

  const copyToClipboard = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    displayToastMessage("Link Copied Successfully!");
  };

  const surveyLink = useMemo(() => {
    if (isStudentQuiz && currentQuiz?.feedbackForm) {
      return createURL(currentQuiz?.feedbackForm.link ?? "");
    } else if (exitTicketActive) {
      return exitTicketActive?.copyLink;
    } else {
      return "";
    }
  }, [isStudentQuiz, currentQuiz, exitTicketActive]);

  const ticketActiveMenu: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <div
          className={styles.activeInfoMenu}
          onClick={(e) => e.stopPropagation()}
        >
          <OutlineIcon width={21} className={styles.outlineIcon} />
          {!isStudentQuiz ? (
            <div className={styles.activeInfo}>
              <span>
                <strong>
                  {Math.round((exitTicketActive?.expiryMinutes ?? 0) / 60)}
                </strong>{" "}
                Minutes Left
              </span>
              <span>
                <strong>{exitTicketActive?.exitTicketCount}</strong> Responses
                Max
              </span>
            </div>
          ) : (
            <div className={styles.activeInfo}>
              <span>
                <strong>
                  {Math.round((currentQuiz?.expiryMinutes ?? 0) / 60)}
                </strong>{" "}
                Minutes Left
              </span>
              <span>
                <strong>
                  {currentQuiz?.feedbackForm?.exitTicketCount ?? 0}
                </strong>{" "}
                Responses Max
              </span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "2",
      label: <div className={styles.dropDownMenuitem}>Cancel</div>,
      onClick: (e) => cancelExitTicket(e),
    },
    {
      key: "3",
      label: (
        <CopyToClipboardComponent text={surveyLink}>
          <div className={styles.dropDownMenuitem}>Copy Link</div>
        </CopyToClipboardComponent>
      ),
      onClick: (e) => copyToClipboard(e),
    },
    {
      key: "4",
      label: <div className={styles.dropDownMenuitem}>Regenerate</div>,
      onClick: (e) => generateExitTicket(e),
    },
  ];

  return (
    <>
      <div
        className={`${styles.content} ${className}`}
        style={{
          paddingBottom:
            isPreview && !isHomeEDApp && !isNewLessonLayout ? 40 : 0,
        }}
      >
        <div className={styles.notifyView}>
          {survey && !isViewExitTicket ? (
            <div
              className={styles.notifySubView2}
              style={{ display: showRememberNotify ? "grid" : "none" }}
            >
              <InfoCircleIcon className={styles.infoCircle} />
              <div className={styles.notifyDetail}>
                <p>Please distribute the Exit Ticket Link to students</p>
              </div>
            </div>
          ) : null}
        </div>
        {showReturningUnit ? (
          <div className={styles.returnView}>
            <div className={styles.closeButton} onClick={handleCloseCounter}>
              <CloseIcon className={styles.closeIcon} />
            </div>
            <div className={styles.progressView}>
              <ProgressAfterConfetti goBack={goBack} />
            </div>
          </div>
        ) : null}

        {children}
        <div
          className={styles.confettiView}
          style={{ display: hideCanvas ? "none" : "block" }}
        >
          <canvas ref={canvasRef} width={width} height={height} />
        </div>
      </div>
      <div
        className={`${styles.actions} ${
          (isPreview || survey) && !isViewExitTicket
            ? !fullScreen
              ? styles.whiteBar
              : styles.blackBar
            : styles.blackBar
        } ${fromModal ? styles.smallPadding : ""}`}
      >
        {leftButton ? (
          <div className={styles.prevAction}>{leftButton}</div>
        ) : (
          <>
            {hasPrevContent ? (
              <div className={styles.prevAction}>
                <Button
                  size={"extraSmall"}
                  onClick={handlePrev}
                  outline={true}
                  label={"PREVIOUS"}
                  className={`${
                    isPreview || survey
                      ? styles.fullScreenWhitePrevious
                      : styles.fullScreenPrevious
                  }`}
                />
              </div>
            ) : isViewExitTicket ? (
              <div className={styles.prevAction}>
                <Button
                  size={"extraSmall"}
                  onClick={handlePrev}
                  outline={true}
                  label={"PREVIOUS"}
                  className={`${
                    survey
                      ? styles.fullScreenWhitePrevious
                      : styles.fullScreenPrevious
                  }`}
                />
              </div>
            ) : (
              <div />
            )}
          </>
        )}
        {Boolean(totalPages) && Boolean(pageNumber) && (
          <div className={styles.pages}>
            <span className={styles.currentPage}>{pageNumber}</span>/
            {totalPages}
          </div>
        )}
        {rightButton ? (
          <div className={styles.endAction}>{rightButton}</div>
        ) : (
          <>
            {isViewExitTicket ? (
              <div className={styles.nextActions}>
                <Button
                  disabled={loading}
                  size={"extraSmall"}
                  onClick={handleInComplete}
                  color={"whitebackground"}
                  label={"Incomplete"}
                  className={styles.action}
                  leftIcon={
                    loading ? <Spinner width={20} height={20} /> : <CrossIcon />
                  }
                />
                <Button
                  disabled={loading}
                  size={"extraSmall"}
                  onClick={handleConfirmComplete}
                  color={"lightgreen"}
                  label={"Complete"}
                  className={styles.action}
                  leftIcon={
                    loading ? <Spinner width={20} height={20} /> : <CheckIcon />
                  }
                />
              </div>
            ) : hasNextContent && !hideNextButton ? (
              <div className={styles.nextActions}>
                {exitTicket &&
                exitTicketActive?.formData &&
                !exitTicketActive.status ? (
                  <Button
                    size={"extraSmall"}
                    onClick={onNext}
                    color={"white"}
                    outline={true}
                    label={"End Lesson"}
                    className={styles.skipExitTicketaction}
                  />
                ) : (
                  <Button
                    size={"extraSmall"}
                    onClick={onNext}
                    color={"white"}
                    label={
                      lastmodule
                        ? startedAlternateLesson
                          ? `END ${section?.name || ""}`
                          : "END LESSON"
                        : "CONTINUE"
                    }
                    className={styles.action}
                  />
                )}
              </div>
            ) : !hasNextContent && (!isPreview || isNewLessonLayout) ? (
              <div className={styles.endAction}>
                <Button
                  size={"extraSmall"}
                  onClick={handleComplete}
                  color={"white"}
                  label={
                    startedAlternateLesson
                      ? `END ${section?.name || ""}`
                      : isStudentQuiz
                      ? "END"
                      : "END LESSON"
                  }
                />
              </div>
            ) : !isHomeEDApp && exitTicket ? (
              <div className={styles.endAction}>
                {!exitTicketActive?.status ? (
                  isStudentQuiz &&
                  currentQuiz?.expiryMinutes &&
                  currentQuiz?.expiryMinutes > 0 ? (
                    <Dropdown
                      menu={{ items: ticketActiveMenu }}
                      trigger={["click"]}
                      placement="bottomRight"
                    >
                      <Button
                        size="small"
                        className={styles.exitTicketActiveButton}
                        leftIcon={
                          cancelling ? (
                            <Spinner width={20} height={20} />
                          ) : (
                            <TicketIcon />
                          )
                        }
                        label={"Assessment Active"}
                        rightIcon={
                          <WarningIcon
                            width={20}
                            height={21}
                            className={styles.warningIcon}
                          />
                        }
                      />
                    </Dropdown>
                  ) : (
                    <Button
                      size={"small"}
                      onClick={handleGenerateExitLink}
                      color={"white"}
                      leftIcon={<TicketIcon />}
                      label={
                        formTypePreProgram
                          ? "Create Quiz Link"
                          : formTypeExitTicket
                          ? "Create Exit Ticket Link"
                          : isStudentQuiz
                          ? "Generate Link"
                          : "Generate Survey Link"
                      }
                    />
                  )
                ) : (
                  <Dropdown
                    menu={{ items: ticketActiveMenu }}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <Button
                      size="small"
                      className={styles.exitTicketActiveButton}
                      leftIcon={<TicketIcon />}
                      label={
                        formTypePreProgram
                          ? "Pre-Program Active"
                          : formTypeExitTicket
                          ? "Exit Ticket Active"
                          : "Research Survey Active"
                      }
                      rightIcon={
                        <WarningIcon
                          width={20}
                          height={21}
                          className={styles.warningIcon}
                        />
                      }
                    />
                  </Dropdown>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>
      {pauseModal && (
        <PauseModal
          open={pauseModal}
          onClose={handlePause}
          onContinue={handleContinue}
        />
      )}
      {isHomeEDApp
        ? completeModal && (
            <CompleteModal
              open={completeModal}
              loading={isMarkLessonCompleteLoading}
              onClose={() => goBack && goBack()}
              onContinue={() => setCompleteModal(false)}
              onComplete={handleCompleteLesson}
              onJustView={handleNotStartedLesson}
              completed={exitTicket}
            />
          )
        : completeModal && (
            <AutoCompleteModal
              open={completeModal}
              loading={isMarkLessonCompleteLoading}
              onClose={() => setCompleteModal(false)}
              onComplete={handleCompleteLesson}
            />
          )}
      {showHereModal && (
        <AdditionalModal
          open={showHereModal}
          onClose={handleGoBack}
          onAdditionalExperience={handleAdditional}
          onExitTicket={gotoExitTicket}
        />
      )}
    </>
  );
}

export default Actions;
